import { CartItem } from '@/models/cart/cart-item'
import * as DateFns from 'date-fns'
import _ from 'lodash'

import * as CurrentTime from '@/models/current-time/functions'
import { ApiCampaign } from '../campaign'
import { ApiProductclass } from '../productclass'

export class ApiNoveltyCampaign extends ApiCampaign {
  get content(): NoveltyContent {
    return this.get('content')
  }

  get rules(): NoveltyRule[] {
    return this.content.novelty_rule.map(rule => createNoveltyRule(rule))
  }

  /**
   * This puts a limit on the whole number of novelties over the all novelty roots.
   */
  get wholeSelectLimit(): number {
    return parseInt(this.get('content.novelty_whole_select_limit'), 10)
  }

  get requiringPrice(): RequiringPrice {
    return this.get('requiring_price')
  }

  canUseForBasePrice(cartItem: CartItem): boolean {
    return this.requiringPrice.usable_brands.includes(cartItem.brandEnglishName || '')
  }

  get noveltyList(): ApiProductclass[] {
    return this.get('using_to', []).map(raw => new ApiProductclass(raw))
  }

  get isVerified(): boolean {
    return this.rulesAreVerified() && this.noveltiesAreDefined() && this.isInPublishingState()
  }

  private rulesAreVerified(): boolean {
    return _.isArray(this.rules) && this.rules.length > 0 && _.every(this.rules, this.ruleIsVerified)
  }

  private ruleIsVerified(rule: NoveltyRule): boolean {
    return !!rule.id && !!rule.requiring_price && !!rule.select_limit
  }

  private noveltiesAreDefined(): boolean {
    return this.rules.map(rule => rule.id).every((id) => {
      return this.noveltyList.some(novelty => novelty.id === id)
    })
  }

  private isInPublishingState(): boolean {
    const currentTime = CurrentTime.create()

    return DateFns.isBefore(currentTime.now, new Date(this.closeAt)) ||
      DateFns.isEqual(currentTime.now, new Date(this.closeAt))
  }
}

export function isNovelty(campaign: ApiCampaign): campaign is ApiNoveltyCampaign {
  return campaign.campaignAppKind === 'novelty'
}

function createNoveltyRule(raw: any): NoveltyRule {
  return {
    id: raw.id,
    requiring_price: parseInt(raw.requiring_price, 10),
    select_limit: parseInt(raw.select_limit, 10),
  }
}

export interface NoveltyRule {
  id: string, // Should be the same as noveltyRootItem
  requiring_price: number, // User can get each novelty 'consuming' this price value within the subtotal amount.
  select_limit: number, // User is allowed to get novelties of this root up to this number.
}

interface NoveltyContent {
  event_content: string,
  novelty_whole_select_limit: number,
  novelty_rule: NoveltyRule[],
}

interface RequiringPrice {
  usable_brands: string[]
}
